import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ChevronDown } from 'lucide-react';
import { WaitlistCTA } from '../WaitlistCTA';
const faqs = [{
  q: 'Do I need a minimum follower count?',
  a: 'No. A post from a 200-follower account that hits the threshold earns the same as one from a larger account. We pay for reach, not reputation.'
}, {
  q: 'Does every approved post earn 10,000 credits?',
  a: "Yes. Every approved post earns at least 10,000 Giga credits, regardless of impressions or views. Above each platform's first threshold, you unlock the cash tiers — or take 1.5x that value in credits."
}, {
  q: 'Should I choose cash or credits?',
  a: "Cash lands in your account through Dub within 5 business days. Credits give you 1.5x the cash value, applied automatically to your Giga workspace. If you're an active Giga user, credits stretch further — you're effectively getting a 50% bonus to run more tasks and automations."
}, {
  q: 'Screenshot or video?',
  a: 'Either works. A clear screenshot of a real Giga output or a short screen recording of Giga working through a task both qualify. The best posts show a real task with a real result — the format matters less than the substance.'
}, {
  q: 'Do you approve posts before I publish?',
  a: 'No — post first, then submit the link through your creator dashboard. We review each submission to confirm it qualifies (real Giga output, #Giga, public, disclosed) and verify the metrics within 48 hours.'
}, {
  q: 'Do I have to use one of the four story angles?',
  a: "No. Any real Giga output qualifies. The angles exist because they're the patterns behind the posts that hit the top tiers. Use them as a starting point, in your own words. Near-identical copies of our examples won't be approved."
}, {
  q: 'How do cash payouts work?',
  a: 'Cash is paid through Dub, our partner platform. Apply once, add payout details, and get paid within 5 business days.'
}, {
  q: 'Can I boost my post or run paid ads on it?',
  a: "You can, but we only reward organic reach. Any boosted, promoted, or paid-amplified impressions don't count toward the view thresholds, and a post that relies on paid reach won't be approved for a cash or credit payout."
}, {
  q: 'Do you check for boosted or promoted reach?',
  a: "Yes. We run an analysis on every submitted post to detect whether any paid boosting or promotion was involved. We pay for organic reach only, so posts with boosted, promoted, or paid-amplified impressions won't be approved."
}, {
  q: 'Do I need to mark posts as a paid partnership?',
  a: "Yes, every post — even if you pick credits, even if the payout hasn't hit yet, even if your account is tiny. The FTC (US) and ASA / CMA (UK) require disclosure based on the intent to be paid, not the moment money lands. Add #ad at the start of your caption (or use the platform's built-in label: Paid partnership on Instagram, Includes paid promotion on YouTube, Branded content on TikTok). Posts without disclosure won't be approved and can't be paid out."
}, {
  q: 'Is the Creator Program available in my country?',
  a: 'The program is open to creators anywhere Dub can send a payout. If cash payouts are unavailable in your region, you can still earn Giga credits, which have no geographic restrictions.'
}];
const INITIAL_COUNT = 4;
export function CreatorFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [showAll, setShowAll] = useState(false);
  const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_COUNT);
  const hiddenCount = faqs.length - INITIAL_COUNT;
  return <div className="py-20 md:py-28 space-y-20 md:space-y-28" data-id="element-2528" data-mp="1b8yl6o2qnq1q">
      <section className="px-6 md:px-8 max-w-3xl mx-auto" data-id="element-2529" data-mp="16h6ri4oad47d">
        <div className="text-center mb-16" data-id="element-2530" data-mp="16fvqudq50ckh">
          <h2 className="font-title font-bold text-4xl md:text-5xl tracking-tight text-[#2B211A]" data-id="element-2531" data-mp="ircjc0eww3tg">
            Frequently asked questions
          </h2>
        </div>
        <div className="space-y-4" data-id="element-2532" data-mp="dqf33tdgpjul">
          {visibleFaqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return <div key={i} className="bg-white rounded-2xl border border-neutral-200 overflow-hidden transition-all hover:shadow-md" data-id="element-2533" data-mp="3i5dcntk5d619" data-mpi={i}>
                
                <button onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full px-6 py-6 flex items-center justify-between text-left focus:outline-none" aria-expanded={isOpen} data-id="element-2534" data-mp="3m5nin67xvwg4">
                  
                  <span className="font-bold text-lg text-[#2B211A] pr-8" data-id="element-2535" data-mp="3vwrw21hyv98u">
                    {faq.q}
                  </span>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-[#2B211A] text-white' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-2536" data-mp="9ptm52av21pw">
                    
                    {isOpen ? <Minus className="w-4 h-4" data-id="element-2537" data-mp="2bhyc488suoxz" /> : <Plus className="w-4 h-4" data-id="element-2538" data-mp="2bir9iilvc23g" />}
                  </span>
                </button>
                <AnimatePresence data-id="element-2539" data-mp="2fja24evl5c6p">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-2540" data-mp="27yz5zjwpb2k3">
                    
                      <div className="px-6 pb-6 text-neutral-500 leading-relaxed" data-id="element-2541" data-mp="2qxuzet0qjcwv">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {hiddenCount > 0 && <div className="mt-8 flex justify-center" data-id="element-2542" data-mp="2hzjbiyr96t84">
            <button onClick={() => {
          setShowAll(prev => !prev);
          if (showAll) setOpenIndex(null);
        }} className="inline-flex items-center justify-center rounded-full bg-transparent border border-neutral-200 hover:bg-neutral-50 px-8 py-3 text-sm font-bold text-[#2B211A] transition-colors focus:outline-none focus:ring-2 focus:ring-[#EA580C]/30" aria-expanded={showAll} data-id="element-2543" data-mp="2cz515a26j76x">
            
              {showAll ? `Hide questions` : `Show All ${faqs.length} Questions`}
            </button>
          </div>}
      </section>

      <section className="px-6 md:px-8 max-w-5xl mx-auto" data-id="element-2544" data-mp="tze3i408cc6b">
        <div className="bg-[#2B211A] rounded-[2.5rem] p-12 md:p-20 text-center relative overflow-hidden shadow-xl" data-id="element-2545" data-mp="y0hjcqia287j">
          <div className="absolute inset-0 dark-grid opacity-50" data-id="element-2546" data-mp="k92s5we7v0pe" />
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative z-10" data-id="element-2547" data-mp="p12xy5gywntw">
            
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-wider mb-6" data-id="element-2548" data-mp="fbjdwwhiqnxg">
              Limited spots available
            </div>
            <h2 className="font-title font-bold text-4xl md:text-6xl tracking-tight text-white mb-6" data-id="element-2549" data-mp="3itfmhgiqy4ii">
              Start earning today.
            </h2>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-10 font-medium" data-id="element-2550" data-mp="3dwfdawqloln6">
              Join the Giga creator program and turn your workflow screenshots
              into cash and credits.
            </p>
            <WaitlistCTA className="inline-block px-8 py-4 bg-[#EA580C] text-white rounded-full font-bold text-lg hover:bg-[#C2410C] transition-colors shadow-lg" data-id="element-2551" data-mp="5n5pt9t7mtbe">
              Get Started for Free
            </WaitlistCTA>
          </motion.div>
        </div>
      </section>
    </div>;
}